export function initExportYtBridge() {
    if (customElements.get('exportyt-widget')) return;

    const style = document.createElement('style');
    style.textContent = `
        exportyt-widget {
            display: block;
            width: 100%;
            background: #111214;
            border: 1px solid #2b2d31;
            border-radius: 12px;
            padding: 16px;
            margin-top: 10px;
            font-family: 'Inter', sans-serif;
            color: #dbdee1;
            box-sizing: border-box;
        }
        .exportyt-header {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 16px;
            border-bottom: 1px solid #2b2d31;
            padding-bottom: 12px;
        }
        .exportyt-header i {
            color: #ff0000;
            font-size: 20px;
        }
        .exportyt-title {
            font-weight: 600;
            font-size: 15px;
            color: #f2f3f5;
        }
        .exportyt-content {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
        .exportyt-input {
            width: 100%;
            padding: 10px;
            background: #1e1f22;
            border: 1px solid #383a40;
            border-radius: 8px;
            color: #dbdee1;
            outline: none;
            box-sizing: border-box;
        }
        .exportyt-input:focus {
            border-color: #5865f2;
        }
        .exportyt-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
        }
        .exportyt-btn {
            background: #5865f2;
            color: white;
            border: none;
            border-radius: 8px;
            padding: 8px 16px;
            cursor: pointer;
            font-size: 13px;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: background 0.2s;
        }
        .exportyt-btn:hover {
            background: #4752c4;
        }
        .exportyt-btn.secondary {
            background: #4e5058;
        }
        .exportyt-btn.secondary:hover {
            background: #6d6f78;
        }
        .exportyt-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
        .exportyt-status {
            font-size: 13px;
            color: #949ba4;
            margin-top: 8px;
        }
        .exportyt-download-link {
            display: inline-block;
            margin-top: 10px;
            padding: 8px 16px;
            background: #23a559;
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
        }
        .exportyt-download-link:hover {
            background: #1d8a4a;
        }
    `;
    document.head.appendChild(style);

    class ExportYtWidget extends HTMLElement {
        private urlInput: HTMLInputElement;
        private statusEl: HTMLElement;
        private actionArea: HTMLElement;
        private baseApi = 'https://api-export.kaywat.codes';

        constructor() {
            super();
            this.innerHTML = `
                <div class="exportyt-header">
                    <i class="fab fa-youtube"></i>
                    <div class="exportyt-title">Export-YT Toolkit</div>
                </div>
                <div class="exportyt-content">
                    <input type="text" class="exportyt-input" placeholder="Paste YouTube URL here..." />
                    <div class="exportyt-actions">
                        <button class="exportyt-btn" data-action="download-video"><i class="fas fa-video"></i> Video</button>
                        <button class="exportyt-btn" data-action="download-audio"><i class="fas fa-music"></i> Audio</button>
                        <button class="exportyt-btn" data-action="transcribe" style="background: #64b5f6;"><i class="fas fa-microphone"></i> Transcribe</button>
                        <button class="exportyt-btn secondary" data-action="export-comments"><i class="fas fa-comments"></i> Comments</button>
                        <button class="exportyt-btn secondary" data-action="raffle"><i class="fas fa-ticket"></i> Raffle</button>
                    </div>
                    <div class="exportyt-action-area"></div>
                    <div class="exportyt-status"></div>
                </div>
            `;
            this.urlInput = this.querySelector('.exportyt-input') as HTMLInputElement;
            this.statusEl = this.querySelector('.exportyt-status') as HTMLElement;
            this.actionArea = this.querySelector('.exportyt-action-area') as HTMLElement;

            const url = this.getAttribute('url');
            if (url) this.urlInput.value = url;

            const action = this.getAttribute('action');
            
            this.querySelectorAll('.exportyt-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    const act = btn.getAttribute('data-action');
                    if (act) this.handleAction(act);
                });
            });

            if (url && action) {
                // Auto trigger if both provided
                setTimeout(() => this.handleAction(action), 100);
            }
        }

        async handleAction(action: string) {
            const url = this.urlInput.value.trim();
            if (!url) {
                this.statusEl.textContent = 'Please enter a valid YouTube URL.';
                return;
            }

            this.statusEl.textContent = 'Processing...';
            this.actionArea.innerHTML = '';
            const btns = this.querySelectorAll('.exportyt-btn');
            btns.forEach((b: any) => b.disabled = true);

            try {
                if (action === 'download-video') {
                    await this.requestDownload(url, '/download-video');
                } else if (action === 'download-audio') {
                    await this.requestDownload(url, '/download-audio');
                } else if (action === 'export-comments') {
                    await this.requestComments(url);
                } else if (action === 'raffle') {
                    await this.requestRaffle(url);
                } else if (action === 'transcribe') {
                    this.statusEl.textContent = 'Launching Transcription Widget...';
                    this.actionArea.innerHTML = `<justrecord-widget yt-url="${url}"></justrecord-widget>`;
                }
            } catch (err: any) {
                this.statusEl.textContent = 'Error: ' + err.message;
            } finally {
                btns.forEach((b: any) => b.disabled = false);
            }
        }

        async requestDownload(url: string, endpoint: string) {
            const res = await fetch(this.baseApi + endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url })
            });
            const data = await res.json();
            if (data.error) throw new Error(data.error);

            this.statusEl.textContent = 'Ready!';
            const link = document.createElement('a');
            link.className = 'exportyt-download-link';
            link.href = this.baseApi + '/exports/' + (data.data?.filename || data.filename || data.file_name || data.data?.file_name);
            link.innerHTML = '<i class="fas fa-download"></i> Download File';
            link.target = '_blank';
            this.actionArea.appendChild(link);
            
            // Auto-trigger download
            setTimeout(() => link.click(), 500);
        }

        async requestComments(url: string) {
            const res = await fetch(this.baseApi + '/export', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url, format: 'csv' })
            });
            
            // Note: The comments endpoint might return binary CSV data directly
            if (res.headers.get('content-type')?.includes('text/csv')) {
                const blob = await res.blob();
                const objUrl = URL.createObjectURL(blob);
                this.statusEl.textContent = 'Comments exported successfully!';
                const link = document.createElement('a');
                link.className = 'exportyt-download-link';
                link.href = objUrl;
                link.download = 'comments.csv';
                link.innerHTML = '<i class="fas fa-file-csv"></i> Download CSV';
                this.actionArea.appendChild(link);
            } else {
                const data = await res.json();
                if (data.error) throw new Error(data.error);
                this.statusEl.textContent = 'Comments exported!';
                
                const btnContainer = document.createElement('div');
                btnContainer.style.display = 'flex';
                btnContainer.style.gap = '10px';
                btnContainer.style.marginTop = '10px';
                
                const link = document.createElement('a');
                link.className = 'exportyt-download-link';
                link.style.marginTop = '0';
                const filename = data.filename || data.file_name || (data.data && (data.data.filename || data.data.file_name));
                link.href = data.download_url ? (this.baseApi + data.download_url) : (this.baseApi + '/exports/' + filename);
                link.innerHTML = '<i class="fas fa-file-csv"></i> Download CSV';
                link.target = '_blank';
                btnContainer.appendChild(link);
                
                // Generate TXT locally from the returned comments array
                let txtContent = '';
                const commentsArray = data.comments || (data.data && data.data.comments);
                if (Array.isArray(commentsArray)) {
                    commentsArray.forEach((c: any, i: number) => {
                        txtContent += `${i + 1}. ${c.author || 'Anonymous'}: ${c.comment || ''}\n`;
                    });
                    const txtBlob = new Blob([txtContent], { type: 'text/plain' });
                    const txtUrl = URL.createObjectURL(txtBlob);
                    
                    const txtLink = document.createElement('a');
                    txtLink.className = 'exportyt-download-link';
                    txtLink.style.marginTop = '0';
                    txtLink.style.background = '#4e5058';
                    txtLink.href = txtUrl;
                    txtLink.download = (data.title || (data.data && data.data.title) || 'comments') + '.txt';
                    txtLink.innerHTML = '<i class="fas fa-file-alt"></i> Download TXT';
                    
                    // Hover effect for inline style
                    txtLink.onmouseover = () => { txtLink.style.background = '#6d6f78'; };
                    txtLink.onmouseout = () => { txtLink.style.background = '#4e5058'; };
                    
                    btnContainer.appendChild(txtLink);
                }
                
                this.actionArea.appendChild(btnContainer);
                
                // Auto-trigger CSV download
                setTimeout(() => link.click(), 500);
            }
        }

        async requestRaffle(url: string) {
            const res = await fetch(this.baseApi + '/pick-winners', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url, winner_count: 1 })
            });
            const data = await res.json();
            if (data.error) throw new Error(data.error);
            
            this.statusEl.textContent = 'Raffle completed!';
            const winners = data.data?.winners || data.winners || [];
            
            if (winners.length > 0) {
                const winnerHtml = winners.map((w: any) => `<div style="margin-top: 10px; padding: 10px; background: #2b2d31; border-radius: 8px;">🎉 <strong>${w.author || w.name || 'Winner'}</strong>: ${w.text || w.comment || ''}</div>`).join('');
                this.actionArea.innerHTML = winnerHtml;
            } else {
                this.statusEl.textContent = 'No winners found (maybe no comments?).';
            }
        }
    }

    customElements.define('exportyt-widget', ExportYtWidget);
}
